// ============================================================
// Вывод инструментов сайта на OBS-оверлей (режим стримера).
// Инструмент (рандомизатор карт, менеджер матча) шлёт сюда состояние
// виджета → сервер кладёт его в состояние оверлея → OBS-страница /overlay
// рисует прокрутку и результат на весь размер оверлея.
// Все запросы «мягкие»: без токена/сети инструмент работает как обычно.
// ============================================================
import { apiRequest } from '../api';
import { state } from '../state';
import { t } from '../i18n';

/** Галочка «Выводить на оверлей OBS» (по умолчанию включена). */
const ENABLED_KEY = 'tools.overlay.enabled';
/** Событие синхронизации: на странице может быть несколько галочек. */
const CHANGED_EVENT = 'tools-overlay-changed';
/** Сервер сообщил число подключённых источников OBS. */
const SOURCES_EVENT = 'tools-overlay-sources';
/** Последнее известное число источников; null — ещё не спрашивали. */
let lastSourceCount: number | null = null;

export interface MapRollOutput {
    mapName: string;
    mapAlt?: string | null;
    mapImage?: string | null;
    modName?: string | null;
    playerCount?: number | null;
    types?: string[];
    settings?: { name: string; value: string }[];
}

/** Участник прогноза для оверлея. */
export interface PredictOutputPlayer {
    name: string;
    race?: string | null;
    chance: number;
    place: number;
    drawn?: boolean;
    games?: number | null;
    winrate?: number | null;
}

export interface MatchPredictOutput {
    mapName?: string | null;
    modName?: string | null;
    players: PredictOutputPlayer[];
}

export function isOverlayOutputEnabled(): boolean {
    try {
        return localStorage.getItem(ENABLED_KEY) !== '0';
    } catch {
        return true;
    }
}

export function setOverlayOutputEnabled(on: boolean): void {
    try {
        localStorage.setItem(ENABLED_KEY, on ? '1' : '0');
    } catch {
        /* приватный режим — просто не запоминаем */
    }
}

/** Вывод возможен: галочка включена и пользователь залогинен (нужен JWT). */
export function overlayOutputReady(): boolean {
    return isOverlayOutputEnabled() && Boolean(state.token);
}

/** Начало прокрутки: оверлей крутит барабаны, пока не придёт результат. */
export async function startOverlayRoll(candidates: { maps: string[]; mods: string[] }): Promise<void> {
    if (!overlayOutputReady()) return;
    await send({ kind: 'map-roll', phase: 'rolling', candidates, data: null });
}

/** Результат розыгрыша (или обновление настроек мода выпавшей карты). */
export async function showOverlayRoll(data: MapRollOutput): Promise<void> {
    if (!overlayOutputReady()) return;
    // Названия для барабанов уже не нужны — оверлей сразу показывает результат
    await send({ kind: 'map-roll', phase: 'result', candidates: { maps: [], mods: [] }, data });
}

/**
 * Начало розыгрыша победителя: оверлей показывает панель прогноза
 * и прокручивает подсветку по строкам (кубик — только в финале).
 */
export async function startOverlayPredictionDraw(data: MatchPredictOutput): Promise<void> {
    if (!overlayOutputReady() || data.players.length < 2) return;
    await send({ kind: 'match-predict', phase: 'rolling', candidates: { maps: [], mods: [] }, data });
}

/** Прогноз матча: состав, шансы и разыгранный победитель — на весь оверлей. */
export async function showOverlayPrediction(data: MatchPredictOutput): Promise<void> {
    if (!overlayOutputReady() || data.players.length < 2) return;
    await send({ kind: 'match-predict', phase: 'result', candidates: { maps: [], mods: [] }, data });
}

/**
 * Галочка «Выводить на оверлей OBS» (по умолчанию включена).
 * Без входа она видна, но неактивна — отправлять нечего.
 */
export function buildOverlayToggle(onToggle?: (on: boolean) => void): HTMLElement {
    const label = document.createElement('label');
    label.className = 'rr-overlay-toggle';
    label.title = t('tools.random_overlay_hint');

    const check = document.createElement('input');
    check.type = 'checkbox';
    check.checked = isOverlayOutputEnabled();
    check.disabled = !state.token;
    if (check.disabled) label.classList.add('is-off');

    // Предупреждение «в OBS нет источника»: без него нажатия уходят в никуда,
    // и кажется, что оверлей «не сработал с первого раза».
    const warn = document.createElement('span');
    warn.className = 'rr-overlay-warn';
    warn.textContent = t('tools.overlay_no_source_short');
    warn.title = t('tools.overlay_no_source');
    warn.hidden = true;
    // Клик по плашке не должен переключать галочку (она внутри label)
    warn.addEventListener('click', (e) => e.preventDefault());

    label.append(check, document.createTextNode(` ${t('tools.random_overlay')}`), warn);

    const paintWarn = (count: number | null): void => {
        if (count === null) {
            warn.hidden = true;
            return;
        }
        lastSourceCount = count;
        warn.hidden = !(check.checked && count === 0);
    };
    paintWarn(lastSourceCount);

    check.addEventListener('change', () => {
        setOverlayOutputEnabled(check.checked);
        // Остальные галочки на странице (этап карты и этап прогноза) показывают то же
        window.dispatchEvent(new Event(CHANGED_EVENT));
        // Выключили — сразу убираем виджет с оверлея
        if (!check.checked) void clearOverlayOutput();
        else void refreshOverlaySources();
        paintWarn(lastSourceCount);
        onToggle?.(check.checked);
    });

    // Галочек может быть несколько — держим их в одном состоянии
    const sync = (): void => {
        if (!label.isConnected) {
            window.removeEventListener(CHANGED_EVENT, sync);
            window.removeEventListener(SOURCES_EVENT, onSources);
            return;
        }
        check.checked = isOverlayOutputEnabled();
        paintWarn(lastSourceCount);
    };
    const onSources = (e: Event): void => {
        const count = (e as CustomEvent<number>).detail;
        paintWarn(typeof count === 'number' ? count : null);
    };
    window.addEventListener(CHANGED_EVENT, sync);
    window.addEventListener(SOURCES_EVENT, onSources);

    // Один раз спрашиваем сервер, подключён ли OBS (если вывод включён)
    if (check.checked) void refreshOverlaySources();
    return label;
}

/** Обновляет сведения о подключённых источниках OBS у всех галочек. */
function publishSourceCount(count: number | null): void {
    if (typeof count !== 'number' || !Number.isFinite(count)) return;
    lastSourceCount = count;
    window.dispatchEvent(new CustomEvent<number>(SOURCES_EVENT, { detail: count }));
}

async function refreshOverlaySources(): Promise<void> {
    if (!state.token) return;
    try {
        const res = await apiRequest<{ subscribers?: number }>('/api/control/state', {
            token: state.token,
        });
        publishSourceCount(typeof res.subscribers === 'number' ? res.subscribers : null);
    } catch {
        // Не смогли узнать — не пугаем пользователя зря
    }
}

/** Убирает виджет с оверлея (галочка выключена / инструмент закрыт). */
export async function clearOverlayOutput(): Promise<void> {
    if (!state.token) return;
    await send({ kind: null });
}

/**
 * Превью игры на оверлей (кнопка «в оверлей» в разделе «Игры» и в модалке игры).
 * Данные собирает сервер по id — клиент шлёт только идентификатор.
 * Возвращает false, если вывести не удалось (нет входа/сети).
 */
export async function showOverlayGame(gameId: number): Promise<boolean> {
    if (!state.token) return false;
    for (let attempt = 0; attempt < 2; attempt++) {
        try {
            const res = await apiRequest<{ state?: { subscribers?: number } }>('/api/control/widget', {
                method: 'POST',
                token: state.token,
                body: { kind: 'game-card', gameId },
            });
            const count = res?.state?.subscribers;
            publishSourceCount(typeof count === 'number' ? count : null);
            // Отправили, но источника нет — вызывающий покажет ⚠ вместо ✓
            return typeof count !== 'number' || count > 0;
        } catch (err) {
            console.warn('[overlay-output] failed to send game card:', err);
            if (attempt === 0) await sleep(250);
        }
    }
    return false;
}

/**
 * Кнопка «показать игру в оверлее» с коротким подтверждением.
 * Вид/класс задаёт вызывающий — в списке игр это иконка, в модалке — кнопка.
 */
export function buildGameOverlayButton(
    gameId: number,
    className: string,
    label: string
): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = className;
    btn.title = t('games.overlay_show_hint');
    btn.textContent = label;

    btn.addEventListener('click', (e) => {
        e.stopPropagation();
        void (async () => {
            btn.disabled = true;
            const ok = await showOverlayGame(gameId);
            btn.textContent = ok ? t('games.overlay_show_done') : '⚠';
            setTimeout(() => {
                btn.textContent = label;
                btn.disabled = false;
            }, 1600);
        })();
    });

    return btn;
}

/** Виджет-сообщения уходят строго по очереди: прокрутка → результат. */
let sendChain: Promise<void> = Promise.resolve();

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

async function send(body: unknown): Promise<void> {
    const task = sendChain.then(async () => {
        // Один повтор: короткий сетевой сбой не должен оставить барабаны
        // крутиться навсегда (результат так и не доедет до оверлея).
        for (let attempt = 0; attempt < 2; attempt++) {
            try {
                const res = await apiRequest<{ state?: { subscribers?: number } }>(
                    '/api/control/widget',
                    { method: 'POST', token: state.token, body }
                );
                publishSourceCount(res?.state?.subscribers ?? null);
                return;
            } catch (err) {
                if (attempt === 1) {
                    console.warn('[overlay-output] failed to send widget:', err);
                    return;
                }
                await sleep(250);
            }
        }
    });
    sendChain = task;
    return task;
}
